import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { AffirmationBackdrop, BackdropReference } from "@/types";

type Phase = "idle" | "generating-image" | "animating" | "done" | "error";

interface GenerateArgs {
  prompt: string;
  references: BackdropReference[];
}

export function useVisionBackdrop() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPhase("idle");
    setImageUrl(null);
    setVideoUrl(null);
    setError(null);
  }, []);

  /**
   * Generate the still image. Returns the image URL on success.
   * Caller can immediately use this as the backdrop, then await animate() in background.
   */
  const generateImage = useCallback(async ({ prompt, references }: GenerateArgs): Promise<string | null> => {
    setPhase("generating-image");
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("generate-vision-image", {
        body: { prompt, references },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      const url: string = data.url;
      setImageUrl(url);
      return url;
    } catch (e: any) {
      console.error("Backdrop image generation failed:", e);
      setError(e.message || "Could not generate backdrop image");
      setPhase("error");
      toast({
        title: "Couldn't create your backdrop image",
        description: e.message || "Please try again.",
        variant: "destructive",
      });
      return null;
    }
  }, []);

  /**
   * Kicks off Kling animation and polls until done. Silently keeps the still image on failure.
   */
  const animate = useCallback(async (sourceImageUrl: string, scenePrompt: string, hasMusic: boolean = false): Promise<string | null> => {
    setPhase("animating");
    try {
      // Refresh session before invoking (memory rule)
      await supabase.auth.getSession();
      const { data: startData, error: startErr } = await supabase.functions.invoke("animate-image", {
        body: {
          imageUrl: sourceImageUrl,
          prompt: `Subtle, ambient living-photograph motion. ${scenePrompt.slice(0, 400)} Gentle environmental movement, no camera cuts.`,
          motionBucket: 8,
          includeAudio: hasMusic,
        },
      });
      if (startErr) throw startErr;
      if (startData?.error) throw new Error(startData.error);
      const predictionId: string = startData.predictionId;

      // Poll up to ~3 min
      const maxAttempts = 90;
      const intervalMs = 2000;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, intervalMs));
        await supabase.auth.getSession();
        const { data: pollData, error: pollErr } = await supabase.functions.invoke("animate-image-poll", {
          body: { predictionId },
        });
        if (pollErr) continue;
        if (pollData?.status === "succeeded" && pollData.url) {
          setVideoUrl(pollData.url);
          setPhase("done");
          return pollData.url;
        }
        if (pollData?.status === "failed" || pollData?.status === "canceled") {
          throw new Error(pollData.error || "Animation failed");
        }
      }
      throw new Error("Animation timed out");
    } catch (e: any) {
      console.warn("Backdrop animation failed (keeping still image):", e);
      // Per UX decision: silently keep the still image
      setPhase("done");
      return null;
    }
  }, []);

  return {
    phase,
    imageUrl,
    videoUrl,
    error,
    generateImage,
    animate,
    reset,
    setImageUrl,
    setVideoUrl,
  };
}

export function buildBackdropRecord(args: {
  prompt: string;
  imageUrl: string;
  videoUrl?: string | null;
  references: BackdropReference[];
}): AffirmationBackdrop {
  return {
    prompt: args.prompt,
    imageUrl: args.imageUrl,
    videoUrl: args.videoUrl ?? undefined,
    references: args.references,
    createdAt: new Date().toISOString(),
  };
}
